import gettext from '@eb/gettext';
import isUndefined from 'lodash/isUndefined';
import { lazyGettext } from '@eb/lazy-gettext';
import { formatQuery } from 'url-lib';
import {
    fetchEB,
    translateServerErrors,
    sdkRequest,
    fetchJSON,
} from '@eb/http';
import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK } from '@eb/social-login-facebook';
import { fetchAuthentication } from './utils';

import { GA_EVENTBRITE } from '../constants/analytics';

const LOGIN_URL = '/ajax/login/';
const SIGNUP_URL = '/ajax/signup_action/';
const SOCIAL_ACTION_URL = '/ajax/social_action/';
const SEND_RESET_PASSWORD_URL = '/api/v3/users/password_reset_email/';
const SEND_SOFT_ACCOUNT_ACTIVATION_EMAIL_URL = '/users/send_activation_email/';
const SET_PASSWORD_URL = '/users/ajax/resetpassword/';
const SEND_LINK_ACCOUNT_URL = '/api/v3/users/send_link_account_email/';

const STANDARD_SERVER_ERROR = gettext(
    'There was an issue with the server, try again',
);

const LOGIN_ERROR_SPEC = {
    password: {
        INVALID: gettext('Incorrect password'),
        REQUIRED: gettext('Password is required'),
    },

    email: {
        UNKNOWN: gettext('This email is not recognized'),
        REQUIRED: gettext('Email is required'),
    },
    captcha: {
        REQUIRED: gettext('This field is required'),
        INCORRECT_SOLUTION: gettext('Provided solution incorrect'),
    },
    __all__: {
        LOCKED_ACCOUNT: gettext('This account has been locked'),
        SUSPENDED_ACCOUNT: gettext('This account has been suspended'),
        CREDENTIALS_MUST_CHANGE: gettext('This password has expired'),
        FORBIDDEN: gettext('Login failed'),
        LOGIN_GATED: STANDARD_SERVER_ERROR,
    },
};

const SIGNUP_ERROR_SPEC = {
    password: {
        MINIMUM_LENGTH_8: gettext('Password must be at least 8 characters'),
        REQUIRED: gettext('Password is required'),
        KNOWN_WEAK_PASSWORD: gettext(
            'Password is highly guessable. Try again.',
        ),
        NOT_COMPLEX_ENOUGH: gettext(
            'Password requires at least one uppercase and one lowercase letter + a number',
        ),
    },

    email: {
        INVALID: gettext('Invalid email'),
        ALREADY_REGISTERED: gettext(
            'Email already associated with an Eventbrite account',
        ),
        REQUIRED: gettext('This field is required'),
    },
    captcha: {
        REQUIRED: gettext('This field is required'),
        INCORRECT_SOLUTION: gettext('Provided solution incorrect'),
    },
    __all__: {
        SIGNUP_GATED: STANDARD_SERVER_ERROR,
    },
};

const ACCOUNT_STATUS_ERROR_SPEC = {
    default: () => STANDARD_SERVER_ERROR,
};

const SET_PASSWORD_ERROR_SPEC = {
    default: (statusCode, error) => {
        let message = STANDARD_SERVER_ERROR;

        if (statusCode === 400 && error && error.error_description) {
            message = error.error_description;
        }

        return message;
    },
};

const NOT_AUTHORIZED_ERROR = lazyGettext(
    'You do not have permission to access the resource you requested.',
);
const SUBSCRIPTION_NOT_FOUND = lazyGettext(
    'The profile you requested does not exist.',
);

const GENERAL_ERROR_SPEC = {
    NOT_AUTHORIZED: () => NOT_AUTHORIZED_ERROR,
    SUBSCRIPTION_NOT_FOUND: () => SUBSCRIPTION_NOT_FOUND,
    USER_HAS_NO_PASSWORD: () => 'USER_HAS_NO_PASSWORD',

    default: () => STANDARD_SERVER_ERROR,
};

const EXTERNAL_IDENTITY_ERROR_SPEC = {
    [SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK]: {
        EXTERNAL_USER_ID_ALREADY_USED: () =>
            lazyGettext(
                'This Facebook profile is already connected to an Eventbrite account. Try using a different Facebook profile.',
            ),
    },
};

export const logInUser = (data) => {
    return fetchAuthentication(LOGIN_ERROR_SPEC, LOGIN_URL, {
        method: 'POST',
        body: formatQuery(data),
    });
};

export const signUpUser = (data) =>
    fetchAuthentication(SIGNUP_ERROR_SPEC, SIGNUP_URL, {
        method: 'POST',
        body: formatQuery(data),
    });

/**
 * This calls the social_action endpoint which is responsible for:
 * 1) setting data into redis
 * 2) trying to login the user
 * 3) returning a logged user
 * 4) returning that the user has another account and cannot be logged
 */
/**
 *
 * @param {{sourceProvider: string, accessToken: string}} data
 */
export const socialLogIn = (data) =>
    fetchAuthentication({}, SOCIAL_ACTION_URL, {
        method: 'POST',
        body: formatQuery(data),
    });

export const sendSoftAccountActivationEmail = (email, destination = '') =>
    fetchAuthentication({}, SEND_SOFT_ACCOUNT_ACTIVATION_EMAIL_URL, {
        method: 'POST',
        body: formatQuery({ email, destination }),
    });

export const sendResetPasswordEmail = (
    email,
    mustLinkAccount = false,
    sourceProvider,
) => {
    const body = {
        email,
        application: 'web',
        must_link_account: mustLinkAccount,
    };

    // external_provider is only sent for facebook and apple
    if (sourceProvider != GA_EVENTBRITE && !isUndefined(sourceProvider)) {
        body['external_provider'] = sourceProvider;
    }
    return fetchEB(SEND_RESET_PASSWORD_URL, {
        headers: {
            'Content-Type': 'application/json',
        },

        method: 'POST',

        body: JSON.stringify(body),
    });
};

export const lookupUser = (email) =>
    sdkRequest('/users/lookup/', {
        method: 'POST',
        body: JSON.stringify({ email }),
    }).catch(translateServerErrors(ACCOUNT_STATUS_ERROR_SPEC));

export const setPassword = (data) =>
    fetchJSON(SET_PASSWORD_URL, {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },

        method: 'POST',
        body: formatQuery(data),
    }).catch(async (response) => {
        await response.json().then((body) => {
            translateServerErrors(SET_PASSWORD_ERROR_SPEC)({
                parsedError: response.status,
                response: body,
            });
        });
    });
export const setLinkedAccountApi = (internal_token) =>
    sdkRequest('/api/v3/auth/link_account_with_token/', {
        method: 'POST',
        body: JSON.stringify({
            internal_token: internal_token,
        }),
    });

export const postAddExternalIdentity = (
    userId = null,
    externalProvider,
    externalToken,
) =>
    sdkRequest(`/api/v3/users/${userId}/external_identities/`, {
        method: 'POST',
        body: JSON.stringify({
            external_identity: {
                external_token: externalToken,
                external_provider: externalProvider,
            },
        }),
    })
        .catch(
            translateServerErrors({
                ...GENERAL_ERROR_SPEC,
                ...EXTERNAL_IDENTITY_ERROR_SPEC[externalProvider],
            }),
        )
        .then(
            ({
                id: externalIdentityId,
                external_user_id: externalUserId,
                external_provider: externalIdentityProvider,
            }) => ({
                externalIdentityId,
                externalUserId,
                externalIdentityProvider,
            }),
        );

/**
 * Given an external provider and a token.
 * If email and the social identity exists, the user is logged in
 * If email exists and doesn't have social identity, it is linked and logged in
 * If none of them exists, create user and social identity, and the user is logged in
 */

export const loginWithToken = ({ externalProvider, externalToken }) =>
    sdkRequest('/auth/provision_from_token/', {
        method: 'POST',
        body: JSON.stringify({
            external_token: externalToken,
            external_provider: externalProvider,
        }),
    })
        .then((response) => response)
        .catch((response) => {
            throw response.parsedError;
        });

export const sendEmailLinkAccountApi = (email, socialProvider) =>
    sdkRequest(SEND_LINK_ACCOUNT_URL, {
        method: 'POST',
        body: JSON.stringify({
            email: email,
            external_provider: socialProvider,
        }),
    });
